<template>
  <div class="org-hori-menu">
    <button
      v-for="(item, index) in menuItems"
      :key="index"
      :class="['menu-btn pointer', { 'active-btn': selectedTab === itemKey(item) }]"
      @click="selectTab(item)"
      text
    >
      <div class="menu-item">
        <!-- Icon as URL/string (default) -->
        <img
          v-if="isStringIcon(item.icon)"
          :src="item.icon"
          alt="icon"
          :class="['icon', { 'icon-active': selectedTab === itemKey(item) }, 'mr-2']"
        />
        <!-- Inline SVG markup support -->
        <span
          v-else-if="item.iconSvg"
          class="icon-svg mr-2"
          v-html="item.iconSvg"
          :class="{ 'icon-active': selectedTab === itemKey(item) }"
        />
        <!-- Icon as component (advanced) -->
        <component
          v-else-if="item.iconComponent"
          :is="item.iconComponent"
          :class="['icon', { 'icon-active': selectedTab === itemKey(item) }, 'mr-2']"
        />
        <span class="item-label">{{ item.label }}</span>
        <span
          v-if="item.label === 'Employees' && count_of_active_employees > 0"
          class="custom-badge"
        >
          {{ count_of_active_employees }}
        </span>
        <span
          v-if="item.label === 'Positions' && count_of_active_positions > 0"
          class="custom-badge"
        >
          {{ count_of_active_positions }}
        </span>
        <span v-if="item.label === 'Roles' && count_of_active_roles > 0" class="custom-badge">
          {{ count_of_active_roles }}
        </span>
        <span
          v-if="item.label === 'Responsibilities' && count_of_active_responsibilities > 0"
          class="custom-badge"
        >
          {{ count_of_active_responsibilities }}
        </span>
        <span
          v-if="item.label === 'Procedures' && count_of_active_procedures > 0"
          class="custom-badge"
        >
          {{ count_of_active_procedures }}
        </span>
        <span
          v-if="item.label === 'Departments' && count_of_active_departments > 0"
          class="custom-badge"
        >
          {{ count_of_active_departments }}
        </span>
        <span
          v-if="item.label === 'Categories' && count_of_active_job_categories > 0"
          class="custom-badge"
        >
          {{ count_of_active_job_categories }}
        </span>
        <span
          v-if="itemKey(item) === 'Job Families' && count_of_active_job_families > 0"
          class="custom-badge"
        >
          {{ count_of_active_job_families }}
        </span>
      </div>
    </button>
    <!-- Filler element to extend the border line to full width -->
    <div class="menu-line-filler"></div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";

const emit = defineEmits(["tab-selected"]);

const props = defineProps({
  menuItems: {
    type: Array,
    required: true,
  },
  initialTab: {
    type: String,
    default: "Dashboard",
  },
  count_of_active_employees: {
    type: Number,
    default: 0,
  },
  count_of_active_positions: {
    type: Number,
    default: 0,
  },
  count_of_active_roles: {
    type: Number,
    default: 0,
  },
  count_of_active_responsibilities: {
    type: Number,
    default: 0,
  },
  count_of_active_procedures: {
    type: Number,
    default: 0,
  },
  count_of_active_departments: {
    type: Number,
    default: 0,
  },
  count_of_active_job_categories: {
    type: Number,
    default: 0,
  },
  count_of_active_job_families: {
    type: Number,
    default: 0,
  },
});

const selectedTab = ref(props.initialTab);

const isStringIcon = (val) => {
  return typeof val === "string" && val.length > 0;
};

// Menu items may carry a stable `key` identifier that differs from their
// visible `label` (e.g. the Job Families tab renders "Families" but its
// routing/state identifier remains "Job Families").
const itemKey = (item) => item.key || item.label;

const selectTab = (item) => {
  selectedTab.value = itemKey(item);
  emit("tab-selected", itemKey(item));
};

watch(
  () => props.initialTab,
  (newVal) => {
    selectedTab.value = newVal;
  },
  { immediate: true }
);
</script>

<style scoped>
.org-hori-menu {
  margin: 8px 0 16px 0;
  display: flex;
  gap: 12px;
}
.org-hori-menu .icon {
  width: 24px;
  height: 24px;
  filter: grayscale(100%); /* Default to gray */
  transition: filter 0.3s ease;
  margin-top: -2px;
}
.org-hori-menu .icon-active {
  filter: brightness(0) saturate(100%) invert(43%) sepia(93%) saturate(2467%) hue-rotate(227deg)
    brightness(88%) contrast(88%);
}
/* Inline SVG styling to mirror the <img> icon behavior */
.org-hori-menu .icon-svg {
  display: inline-flex;
}
.org-hori-menu .icon-svg svg {
  width: 24px;
  height: 24px;
  filter: grayscale(100%);
  transition: filter 0.3s ease;
}
.org-hori-menu .icon-svg.icon-active svg {
  filter: brightness(0) saturate(100%) invert(43%) sepia(93%) saturate(2467%) hue-rotate(227deg)
    brightness(88%) contrast(88%);
}
.org-hori-menu .menu-btn {
  box-sizing: border-box;
  margin: 0;
  height: 32px;
  padding: 0 4px 12px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  border: none;
  background: none;
  border-bottom: 2px solid #eaecf0;
  color: #475467;
  cursor: pointer;
  transition: all 0.3s ease;
}
.org-hori-menu .active-btn {
  color: #7f56d9;
  border-bottom: 2px solid #d6bbfb;
}
.org-hori-menu .menu-item {
  display: flex;
  align-items: center;
  white-space: nowrap;
}
.org-hori-menu .item-label {
  font-weight: 600;
}
.org-hori-menu .custom-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 9999px;
  background-color: #f9fafb;
  border: 1px solid #eaecf0;
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

/* Filler element to extend the border line to full width */
.org-hori-menu .menu-line-filler {
  flex: 1;
  box-sizing: border-box;
  border-bottom: 2px solid #eaecf0;
  height: 32px;
}
</style>
